/* eslint-disable no-unused-vars */
/**
 * This service is a bridge between the application and whatever analytics
 * software is used.
 *
 * By defining the REACT_APP_ANALYTICS environment variable we are able to
 * either export the real or mocked implementation, so that from the application
 * point of view nothing changes and it can continue calling the analytics logic
 * as per usuall.
 */
// @ts-ignore legacy package typing is not available in this workspace
import { cloneDeep, set, merge, get } from 'lodash'

declare global {
  interface Window {
    du_tracking_pagename?: string
    du_digitalData?: any
    _satellite?: {
      track?: (action: string) => void
      pageBottom?: () => void
    }
    __CJ_DEBUG_ANALYTICS?: boolean
  }
}

interface AnalyticsKeys {
  pageName: () => string
  condition: () => string
  viewChange: () => string
  formType: () => string
  formName: () => string
  linkInfo: (eventCount: number) => string
  eventType: (eventCount: number) => string
  eventAction: (eventCount: number) => string
  errorCode: () => string
  errorMessage: () => string
  errorCausingURL: () => string
}

interface AnalyticsImplementation {
  default: Record<string, any>
  datalayer: Record<string, any>
  pageName: string | null
  isLive?: boolean
  pageBottom: () => void
  setDefaults: (obj: Record<string, any>) => void
  addToDatalayer: (additions: Record<string, any>) => void
  setTrackingData: (additions: Record<string, any>) => void
  trackViewChange: () => void
  trackPage: () => void
  trackInteraction: () => void
  updateScreenSize: () => void
  track: (action: string) => void
  keys: AnalyticsKeys
}

const mock: AnalyticsImplementation = {
  default: {},
  datalayer: {},
  pageName: null,
  pageBottom: function pageBottom() {},

  setDefaults: function setDefaults(this: any, obj: Record<string, any>) {
    Object.assign(this.default, obj)
  },
  addToDatalayer(this: any, additions: Record<string, any>) {
    Object.keys(additions).forEach(key => {
      set(this.datalayer, key, additions[key])
    })
  },

  setTrackingData(this: any, additions: Record<string, any>) {
    const clonedDefaultDatalayer = cloneDeep(this.default)
    const trackingData = {}
    window.du_tracking_pagename = additions[this.keys.pageName()]
    Object.keys(additions).forEach(key => {
      set(trackingData, key, additions[key])
    })
    window.du_digitalData = merge(
      {},
      clonedDefaultDatalayer,
      this.datalayer,
      trackingData
    )
  },
  trackViewChange: function viewChange(this: any) {
    this.track('viewChange')
  },

  trackPage: function page(this: any) {
    this.addToDatalayer({
      [this.keys.pageName()]: get(window.du_digitalData, this.keys.pageName()),
    })
    this.track('page')
  },

  trackInteraction: function interaction(this: any) {
    this.track('interaction')
  },

  updateScreenSize: function updateScreenSize(this: any) {
    if (window.innerWidth > 1920) {
      this.addToDatalayer({
        'design.browserResolutionBreakpoint': 'xxl',
      })
    } else if (window.innerWidth >= 1600) {
      this.addToDatalayer({
        'design.browserResolutionBreakpoint': 'xl',
      })
    } else if (window.innerWidth >= 1280) {
      this.addToDatalayer({
        'design.browserResolutionBreakpoint': 'l',
      })
    } else if (window.innerWidth >= 960) {
      this.addToDatalayer({
        'design.browserResolutionBreakpoint': 'm',
      })
    } else if (window.innerWidth >= 720) {
      this.addToDatalayer({
        'design.browserResolutionBreakpoint': 's',
      })
    } else if (window.innerWidth >= 480) {
      this.addToDatalayer({
        'design.browserResolutionBreakpoint': 'xs',
      })
    }
  },

  track: function track(this: any, action: string) {
    if (typeof action !== 'string') {
      // eslint-disable-next-line no-console
      console.warn('Legacy tracking code detected')
      return
    }
    // If no viewChange on data, set it as same value as pageName
    if (!get(window.du_digitalData, this.keys.viewChange())) {
      this.setTrackingData(window.du_digitalData, {
        [this.keys.viewChange()]: get(window.du_digitalData, this.keys.pageName()),
      })
    }
    // eslint-disable-next-line no-underscore-dangle
    if (!implementation.isLive || window.__CJ_DEBUG_ANALYTICS) {
      // eslint-disable-next-line no-console
      console.log(
        `track ${action}\n pageName: ${get(
          window.du_digitalData,
          this.keys.pageName()
        )}\n viewChange: ${get(
          window.du_digitalData,
          this.keys.viewChange()
        )}\n event0.type: ${get(
          window.du_digitalData,
          this.keys.eventType(0)
        )}\n event0.action: ${get(
          window.du_digitalData,
          this.keys.eventAction(0)
        )}\n event0.linkinformation: ${get(
          window.du_digitalData,
          this.keys.linkInfo(0)
        )}\n\n Datalayer:`,
        window.du_digitalData
      )
    }
    // eslint-disable-next-line no-unused-expressions
    window._satellite && window._satellite.track && window._satellite.track(action)
  },
  keys: {
    pageName: () => 'core.pageInfo.pageName',
    condition: () => 'product[0].vehicleModel[0].condition',
    viewChange: () => 'core.attributes.viewChange',
    formType: () => 'form.type',
    formName: () => 'form.name',
    linkInfo: eventCount => `event[${eventCount}].eventInfo.linkInformation`,
    eventType: eventCount => `event[${eventCount}].eventInfo.eventType`,
    eventAction: eventCount => `event[${eventCount}].eventInfo.eventAction`,
    errorCode: () => 'error.errorCode',
    errorMessage: () => 'error.errorMessage',
    errorCausingURL: () => 'error.errorCausingURL',
  },
}

const real: AnalyticsImplementation = {
  ...mock,
  isLive: true,
  pageBottom: function pageBottom() {
    // eslint-disable-next-line no-unused-expressions
    window._satellite && window._satellite.pageBottom && window._satellite.pageBottom()
  },
}

let implementation = mock

if (process.env.REACT_APP_ANALYTICS) {
  implementation = real
}

export { implementation as Analytics }
